import type { ReactNode } from 'react';
import { cn } from '@/shared/lib';
import { FieldMarker } from './parts/field-marker';
import { styles } from './styles';

export { FieldMarker } from './parts/field-marker';

/**
 * A field's visible label, with an explicit required / optional marker.
 *
 * WHY THIS EXISTS
 * ---------------
 * A form that does not say which fields are required makes the user guess, and
 * the guess is always the same one: "everything is required, so I will fill in
 * everything." That is the failure mode this component removes. On a form with
 * eight fields where two are required, an unmarked form costs the user six
 * unnecessary decisions and makes the two that matter invisible.
 *
 * The marker itself is `FieldMarker`; this component owns the label element and
 * the layout that keeps the marker at the end of the name rather than wrapping
 * under it — a marker on its own line reads as a second label.
 */
export function FieldLabel({
  htmlFor,
  children,
  required = false,
  optional = false,
  visuallyHidden = false,
  className,
}: {
  /** The control this label names. Omitted when the label wraps its control. */
  htmlFor?: string;
  /** The field's name. */
  children: ReactNode;
  /** Marks the field as required. Renders "Required". */
  required?: boolean;
  /** Marks the field as optional. Renders "Optional". */
  optional?: boolean;
  /** Keeps the label in the accessibility tree while hiding it visually. */
  visuallyHidden?: boolean;
  className?: string;
}): ReactNode {
  return (
    <label
      {...(htmlFor ? { htmlFor } : {})}
      className={cn(styles.label, visuallyHidden && styles.visuallyHidden, className)}
    >
      <span className={styles.text}>{children}</span>
      <FieldMarker required={required} optional={optional} />
    </label>
  );
}
